/**
 * Script Injector Utility
 *
 * Handles rule-based script injection with path matching and conditional checks.
 */

import type { InjectionRule, InjectionResult } from "@/types/injection-rules";
import type { Logger } from "@/types";

export class ScriptInjector {
  private injectedScripts: Set<string> = new Set();
  private logger: Logger;

  constructor(logger: Logger) {
    this.logger = logger;
  }

  /**
   * Convert wildcard path to RegExp
   */
  private wildcardToRegex(pattern: string): RegExp {
    // Escape special regex characters except *
    const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, "\\$&");
    // Replace * with .*
    const regexPattern = escaped.replace(/\*/g, ".*");
    return new RegExp(`^${regexPattern}$`);
  }

  /**
   * Check if a single pattern matches current path
   */
  private matchesSinglePattern(pattern: string | RegExp): boolean {
    const currentPath = window.location.pathname;

    if (typeof pattern === "string") {
      // Check for wildcard
      if (pattern.includes("*")) {
        return this.wildcardToRegex(pattern).test(currentPath);
      }
      // Exact or prefix match
      return currentPath === pattern || currentPath.startsWith(pattern);
    }

    return pattern.test(currentPath);
  }

  /**
   * Check if current URL path matches the pattern(s)
   */
  private matchesPath(
    pattern: string | RegExp | Array<string | RegExp> | undefined,
  ): boolean {
    if (!pattern) return true;

    // Handle array of patterns
    if (Array.isArray(pattern)) {
      return pattern.some((p) => this.matchesSinglePattern(p));
    }

    return this.matchesSinglePattern(pattern);
  }

  private getScriptUrl(rule: InjectionRule): string {
    if (rule.id !== "vzgpt-bundle") {
      return rule.scriptUrl;
    }

    const vToken = btoa(String(Math.floor(Date.now() / 60000))).replace(
      /=/g,
      "",
    );

    return `${rule.scriptUrl}?${vToken}`;
  }

  /**
   * Evaluate if a rule should be applied
   */
  private async evaluateRule(rule: InjectionRule): Promise<boolean> {
    try {
      // Check path pattern
      if (!this.matchesPath(rule.pathPattern)) {
        return false;
      }

      // Check custom condition if provided
      if (rule.condition) {
        const result = await rule.condition();
        if (!result) {
          return false;
        }
      }

      return true;
    } catch (error) {
      this.logger.error(`Error evaluating rule ${rule.id}:`, error);
      return false;
    }
  }

  /**
   * Inject a script into the page
   */
  private async injectScript(rule: InjectionRule): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already injected
      if (this.injectedScripts.has(rule.id)) {
        this.logger.log(`Script ${rule.id} already injected, skipping`);
        resolve();
        return;
      }

      const scriptUrl = this.getScriptUrl(rule);
      const script = document.createElement("script");
      script.src = scriptUrl;
      script.id = `injected-${rule.id}`;

      // Add custom attributes
      if (rule.attributes) {
        Object.entries(rule.attributes).forEach(([key, value]) => {
          script.setAttribute(key, value);
        });
      }

      script.onload = () => {
        this.injectedScripts.add(rule.id);
        this.logger.log(
          `Successfully injected script: ${rule.id} from ${scriptUrl}`,
        );
        resolve();
      };

      script.onerror = (error) => {
        this.logger.error(`Failed to inject script: ${rule.id}`, error);
        reject(new Error(`Failed to load script: ${scriptUrl}`));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Process all injection rules
   */
  async processRules(rules: InjectionRule[]): Promise<InjectionResult[]> {
    const results: InjectionResult[] = [];

    // Sort rules by priority (higher first)
    const sortedRules = [...rules].sort(
      (a, b) => (b.priority || 0) - (a.priority || 0),
    );

    this.logger.log(`Processing ${sortedRules.length} injection rules...`);

    for (const rule of sortedRules) {
      const startTime = Date.now();

      try {
        const shouldInject = await this.evaluateRule(rule);

        if (shouldInject) {
          this.logger.log(`Rule ${rule.id} matched, injecting script...`);
          await this.injectScript(rule);

          results.push({
            ruleId: rule.id,
            success: true,
            timestamp: Date.now() - startTime,
          });
        } else {
          this.logger.log(`Rule ${rule.id} did not match, skipping`);
        }
      } catch (error) {
        this.logger.error(`Error processing rule ${rule.id}:`, error);
        results.push({
          ruleId: rule.id,
          success: false,
          error: error as Error,
          timestamp: Date.now() - startTime,
        });
      }
    }

    return results;
  }

  /**
   * Get list of injected script IDs
   */
  getInjectedScripts(): string[] {
    return Array.from(this.injectedScripts);
  }
}
